/* ============================================================
   omi-animate.js
   Scroll-triggered entrance animations — IntersectionObserver
   ============================================================
   Pairs with omi-animate.css.

   WRAPPER:  .omi-animate
   TARGETS:  any child (or the wrapper itself) with a class
             matching  animate-*  (e.g. animate-fade-down).

   DATA ATTRS (on wrapper):
     data-stagger="200"    ms between each child       (default 150)
     data-threshold="0.3"  IO threshold                (default 0.2)
     data-reset="false"    disable reset on scroll-up  (default true)
     data-delay="400"      base delay before first child

   DATA ATTRS (on child):
     data-delay="300"      extra delay for this element
   ============================================================ */

(function () {
  "use strict";

  /* ----------------------------------------------------------
     Helpers
     ---------------------------------------------------------- */

  /** Cheap old-iOS check — reuse your existing one if available. */
  function isOldiOS() {
    if (typeof window.isOldiOS === "function") return window.isOldiOS();
    var ua = navigator.userAgent;
    var match = ua.match(/OS (\d+)_/);
    return match ? parseInt(match[1], 10) < 14 : false;
  }

  /** CSS selector that matches every animate-* class we ship. */
  var ANIMATION_SELECTOR = [
    ".animate-fade-down",
    ".animate-fade-up",
    ".animate-fade-in",
    ".animate-fade-left",
    ".animate-fade-right",
    ".animate-scale-up",
    ".animate-blur-in",
    ".animate-fade-up-scale",
    ".animate-fade-down-scale"
  ].join(",");

  /* ----------------------------------------------------------
     Scroll-direction tracker  (single listener, shared state)
     ---------------------------------------------------------- */
  var scrollDir = "down";
  var lastY = window.scrollY;

  window.addEventListener(
    "scroll",
    function () {
      var y = window.scrollY;
      scrollDir = y >= lastY ? "down" : "up";
      lastY = y;
    },
    { passive: true }
  );

  /* ----------------------------------------------------------
     Core init
     ---------------------------------------------------------- */
  function initOmiAnimate() {
    /* Bail on old iOS (no IO support / janky behaviour) */
    if (isOldiOS()) return;

    /* Respect prefers-reduced-motion — CSS handles visuals,
       but we skip the observer entirely so .is-animated is
       added immediately (elements are visible from the start). */
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      document.querySelectorAll(".omi-animate").forEach(function (wrapper) {
        getTargets(wrapper).forEach(function (el) {
          el.classList.add("is-animated");
        });
      });
      return;
    }

    var wrappers = document.querySelectorAll(".omi-animate");
    if (!wrappers.length) return;

    wrappers.forEach(function (wrapper) {
      var threshold = parseFloat(wrapper.dataset.threshold) || 0.2;

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            handleEntry(entry, wrapper);
          });
        },
        { threshold: threshold }
      );

      observer.observe(wrapper);
    });
  }

  /* ----------------------------------------------------------
     Gather animation targets — children + self if applicable
     ---------------------------------------------------------- */
  function getTargets(wrapper) {
    var children = Array.from(
      wrapper.querySelectorAll(ANIMATION_SELECTOR)
    );

    /* If the wrapper itself carries an animate-* class, include it */
    if (wrapper.matches(ANIMATION_SELECTOR)) {
      children.unshift(wrapper);
    }

    return children;
  }

  /* ----------------------------------------------------------
     Handle a single IO entry
     ---------------------------------------------------------- */
  function handleEntry(entry, wrapper) {
    var stagger   = parseInt(wrapper.dataset.stagger, 10)  || 150;
    var baseDelay = parseInt(wrapper.dataset.delay, 10)    || 0;
    var shouldReset = wrapper.dataset.reset !== "false";
    var targets = getTargets(wrapper);

    if (entry.isIntersecting) {
      targets.forEach(function (el, i) {
        var childDelay = parseInt(el.dataset.delay, 10) || 0;
        var totalDelay = baseDelay + i * stagger + childDelay;

        if (totalDelay === 0) {
          el.classList.add("is-animated");
        } else {
          setTimeout(function () {
            el.classList.add("is-animated");
          }, totalDelay);
        }
      });
    } else if (shouldReset && scrollDir === "up") {
      targets.forEach(function (el) {
        el.classList.remove("is-animated");
      });
    }
  }

  /* ----------------------------------------------------------
     Boot — run on DOMContentLoaded or immediately if late
     ---------------------------------------------------------- */
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initOmiAnimate);
  } else {
    initOmiAnimate();
  }

  /* Expose for Shopify section re-renders / AJAX navigations */
  window.initOmiAnimate = initOmiAnimate;
})();